import type {
  Actuator,
  Equality,
  Joint,
  Tendon,
  TendonJointRef,
  TendonSpatialRef,
  TendonUUID,
} from "../../types/scene";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import type { SceneStoreGetState, SceneStoreSetState } from "../sceneStore.types";
import type { TendonActions, TendonInput } from "./actionTypes";
import { withHistory } from "./withHistory";
import { namesFromRecord, nextUniqueName } from "../../utils/uniqueNames";
import { logger } from "../../utils/logger";

const ensureTendonUuid = (tendon: Partial<Tendon> | Tendon): Tendon => {
  if (tendon.uuid) return tendon as Tendon;
  return { ...tendon, uuid: generateUuid() as TendonUUID } as Tendon;
};

type JointRecordByString = Record<string, Joint | undefined>;
type TendonInputShape = Partial<Tendon> & {
  spatial?: unknown;
  attributes?: unknown;
};
type TendonWithRequiredIdentity = TendonInputShape & {
  uuid: TendonUUID;
  name: string;
};
type TendonJointInput = string | { joint?: unknown; coef?: unknown };
type TendonSpatialInput = {
  kind?: unknown;
  site?: unknown;
  geom?: unknown;
  sidesite?: unknown;
  divisor?: unknown;
};
type ActuatorRecordByString = Record<string, Actuator>;
type EqualityRecordByString = Record<string, Equality>;

// Normalize a tendon's joint members so MuJoCo never sees duplicates,
// non-finite coefficients, or silently-invalid zero coefficients. We keep
// the tendon in the store even if it ends up with <2 members (so the UI
// can still edit a mid-creation tendon); the XML exporter skips those.
const sanitizeTendonJoints = (
  joints: unknown,
  tendonName: string,
  knownJoints: JointRecordByString = {},
  options: { warnWhenIncomplete?: boolean } = {}
): TendonJointRef[] => {
  const { warnWhenIncomplete = true } = options;
  if (!Array.isArray(joints)) return [];
  const seen = new Set<string>();
  const out: TendonJointRef[] = [];
  joints.forEach((entry: TendonJointInput) => {
    const jointUuid =
      typeof entry === "string" ? entry : typeof entry?.joint === "string" ? entry.joint : null;
    if (!jointUuid || seen.has(jointUuid)) return;
    const joint = knownJoints[jointUuid];
    if (joint && joint.kind !== "hinge" && joint.kind !== "slide") {
      logger.warn(
        `[tendonActions] Removed joint "${joint.name || jointUuid}" from tendon "${tendonName}" because MuJoCo fixed tendons only support hinge or slide joints.`
      );
      return;
    }
    const rawCoef = typeof entry === "object" && entry !== null ? Number(entry.coef) : NaN;
    const coef = Number.isFinite(rawCoef) ? rawCoef : 0.5;
    seen.add(jointUuid);
    out.push({ joint: jointUuid as TendonJointRef["joint"], coef });
  });
  if (warnWhenIncomplete && out.length < 2) {
    logger.warn(
      `[tendonActions] Tendon "${tendonName}" has fewer than 2 unique joint members; it will be excluded from exported MJCF until fixed.`
    );
  }
  return out;
};

const sanitizeTendonAttributes = (attributes: unknown): Record<string, string> | undefined => {
  if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) return undefined;
  const out: Record<string, string> = {};
  Object.entries(attributes as Record<string, unknown>).forEach(([key, value]) => {
    if (!key || value === undefined || value === null) return;
    out[key] = String(value);
  });
  return Object.keys(out).length > 0 ? out : undefined;
};

const getScalarJointUuids = (knownJoints: JointRecordByString = {}): string[] =>
  Object.values(knownJoints)
    .filter(
      (joint): joint is Joint => !!joint?.uuid && (joint.kind === "hinge" || joint.kind === "slide")
    )
    .map((joint) => String(joint.uuid));

const completeFixedTendonJoints = (
  joints: unknown,
  knownJoints: JointRecordByString = {}
): TendonJointRef[] => {
  const existing = sanitizeTendonJoints(joints, "", knownJoints, { warnWhenIncomplete: false });
  const seen = new Set(existing.map((entry) => String(entry.joint)));
  const completed = [...existing];
  getScalarJointUuids(knownJoints).forEach((jointUuid) => {
    if (completed.length >= 2 || seen.has(jointUuid)) return;
    seen.add(jointUuid);
    completed.push({ joint: jointUuid as TendonJointRef["joint"], coef: 0.5 });
  });
  return completed;
};

const sanitizeSpatialRoute = (route: unknown, tendonName: string): TendonSpatialRef[] => {
  if (!Array.isArray(route)) return [];
  const out: TendonSpatialRef[] = [];
  route.forEach((entry: TendonSpatialInput) => {
    if (!entry || typeof entry !== "object") return;
    if (entry.kind === "site") {
      if (typeof entry.site === "string" && entry.site) {
        out.push({ kind: "site", site: entry.site });
      }
      return;
    }
    if (entry.kind === "geom") {
      if (typeof entry.geom !== "string" || !entry.geom) return;
      const next: TendonSpatialRef = { kind: "geom", geom: entry.geom };
      if (typeof entry.sidesite === "string" && entry.sidesite) {
        next.sidesite = entry.sidesite;
      }
      out.push(next);
      return;
    }
    if (entry.kind === "pulley") {
      const rawDivisor = Number(entry.divisor);
      out.push({
        kind: "pulley",
        ...(Number.isFinite(rawDivisor) ? { divisor: rawDivisor } : {}),
      });
    }
  });
  if (out.length === 0) {
    logger.warn(
      `[tendonActions] Spatial tendon "${tendonName}" has no valid route elements; it will be excluded from exported MJCF until fixed.`
    );
  }
  return out;
};

const normalizeTendonRecord = (
  map: Record<string, Tendon | Partial<Tendon>> | undefined,
  knownJoints: JointRecordByString = {}
): Record<TendonUUID, Tendon> => {
  const byId: Record<TendonUUID, Tendon> = {};
  Object.entries(map || {}).forEach(([key, tendon]) => {
    if (!tendon) return;
    const withName = tendon.name ? tendon : { ...tendon, name: key };
    const withUuid = ensureTendonUuid(withName) as TendonWithRequiredIdentity;
    const kind = withUuid.kind || "fixed";
    const sanitized: Tendon = {
      ...withUuid,
      kind,
      joints:
        kind === "fixed"
          ? sanitizeTendonJoints(withUuid.joints, withUuid.name || key, knownJoints)
          : [],
      spatial:
        kind === "spatial"
          ? sanitizeSpatialRoute(withUuid.spatial, withUuid.name || key)
          : undefined,
      attributes: sanitizeTendonAttributes(withUuid.attributes),
    };
    byId[sanitized.uuid] = sanitized;
  });
  return byId;
};

export const createTendonActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): TendonActions => ({
  setTendons: (tendons: Record<string, Tendon | Partial<Tendon>>) =>
    set((state) =>
      create(state, (draft) => {
        draft.tendons = normalizeTendonRecord(tendons, draft.joints || {});
      })
    ),

  addTendon: withHistory((tendonInput: TendonInput): TendonUUID => {
    const withUuid = ensureTendonUuid(tendonInput as Partial<Tendon>) as TendonWithRequiredIdentity;
    const existingNames = namesFromRecord(get().tendons);
    const finalName = nextUniqueName(withUuid.name || "tendon", existingNames);
    const tendon: Tendon = {
      ...withUuid,
      name: finalName,
      kind: withUuid.kind || "fixed",
      joints:
        (withUuid.kind || "fixed") === "fixed"
          ? completeFixedTendonJoints(withUuid.joints, get().joints || {})
          : [],
      spatial:
        (withUuid.kind || "fixed") === "spatial"
          ? sanitizeSpatialRoute(withUuid.spatial, finalName)
          : undefined,
      attributes: sanitizeTendonAttributes(withUuid.attributes),
    };
    set((state) =>
      create(state, (draft) => {
        draft.tendons[tendon.uuid] = tendon;
      })
    );
    return tendon.uuid;
  }),

  updateTendon: withHistory((tendonUuid: TendonUUID, patch: Partial<Tendon>): void => {
    set((state) =>
      create(state, (draft) => {
        const current = draft.tendons?.[tendonUuid];
        if (!current) return;
        const next = {
          ...current,
          ...patch,
          uuid: tendonUuid,
        } as Tendon & TendonInputShape;
        if ((next.kind || "fixed") === "spatial") {
          next.joints = [];
          next.spatial = sanitizeSpatialRoute(next.spatial, next.name || tendonUuid);
        } else {
          next.joints = sanitizeTendonJoints(
            next.joints,
            next.name || tendonUuid,
            draft.joints || {}
          );
          next.spatial = undefined;
        }
        next.attributes = sanitizeTendonAttributes(next.attributes);
        draft.tendons[tendonUuid] = next;
      })
    );
  }),

  removeTendon: withHistory((tendonUuid: TendonUUID): void => {
    set((state) =>
      create(state, (draft) => {
        if (!draft.tendons) return;
        const current = draft.tendons[tendonUuid];
        const deletedTargets = new Set<string>([String(tendonUuid)]);
        if (current?.uuid) deletedTargets.add(String(current.uuid));
        if (current?.name) deletedTargets.add(String(current.name));
        delete draft.tendons[tendonUuid];

        const actuators = draft.actuators as ActuatorRecordByString;
        Object.entries(actuators).forEach(([actuatorUuid, actuator]) => {
          if (!actuator) return;
          const target = actuator.tendonUuid || actuator.tendon;
          if (target && deletedTargets.has(String(target))) {
            delete actuators[actuatorUuid];
          }
        });

        const equalities = draft.equality as EqualityRecordByString;
        Object.entries(equalities).forEach(([equalityUuid, equality]) => {
          if (!equality) return;
          if (
            equality.objtype === "tendon" &&
            ((equality.obj1 && deletedTargets.has(String(equality.obj1))) ||
              (equality.obj2 && deletedTargets.has(String(equality.obj2))))
          ) {
            delete equalities[equalityUuid];
          }
        });
      })
    );
  }),
});
